import { clearEditContent, upsertContent, batchUpsertContent, removeContent, setEditContent, setEditInline, stopEditContent, updateEditContent, setProject, upsertPage, initializeAdmin, initializeAdminProject, removePage, setOrganization, createLLMContent, addMediaFromUrl, fetchDocumentationLinks, cloneTemplateFromPage, setTemplateCloning, fetchTemplatePages, optimisticReorder } from 'actions/adminActions'
import { useAppSelector, useAppDispatch, getMediaSelector, usePage, useHybridContent, useLocalStorage, useDownloadMedia, useAdminPermissions } from 'app/hooks'
import { DynamicContentType, CustomEventType, EditorFieldType, PromptOptions, PromptType, ThumbSize, AnalyticsEvent, FormFieldType, PageType, LocationType, LLMGenerateType, PageTypeId, UserRole } from 'app/types'
import { Button, DropdownMenu, ErrorBoundary, Icon, IconButton, Spinner, Media, Toggle, DocLinkButton } from 'components'
import React, { useEffect, useRef, useState } from 'react'
import { MemoryRouter } from 'react-router-dom'
import { GenericEditor } from 'views/AdminPage/Views/GenericEditor'
import * as fnc from 'helpers/fnc'
import { navigate, removeAppContent, resolvePrompt, showPrompt, updateAppContent, updateOrganization, updateProjectCurrent, updatePrompt } from 'actions/appActions'
import { organizationContent, organizationRoutes, organizationStaticContent, projectStaticContent } from 'app/transformers'
import { logger } from 'helpers/logger'
import { CustomContent } from './CustomContent'
import { defaultFormFields, pageIcons } from 'app/constants'
import { docLinks } from 'services/DocumentationService'
import { getPageTypeName } from 'helpers/project'
import { getPageTypeDocumentationLink } from 'helpers/pageDocumentation'
import { LLMContentHelper } from './LLMContentHelper'
import { DynamicPage } from 'views/DynamicPage'
import { TemplateSelector } from './TemplateSelector'
import { CloneTemplateSelector } from './CloneTemplateSelector'
import { ReorderManager } from './ReorderManager'

enum EditorTab {
    Content = 'Content',
    Pages = 'Pages',
    Blogs = 'Blogs',
}

const pageTagEnum = (data, config) => {
    let source = config.app ? config.app : config.organization
    const matchingPages = source?.pages.filter((x) => x.pageType == PageType.Blog)
    let tagSet = new Set(data.tags?.split(','))
    matchingPages?.forEach((x) => {
        let tags = x.tags ? x.tags.split(',') : []
        if (tags.length == 0) return
        tagSet = tagSet.union(new Set(tags))
    })
    let ret = Array.from(tagSet).filter((x) => x.length > 0)
    return ret
}
const excludePageTypes = [PageType.Admin, PageType.Favourite, PageType.Compare, PageType.Developments, PageType.Tours, PageType.Building]
const excludeContentTypes = [DynamicContentType.Features, DynamicContentType.Highlight, DynamicContentType.Developers, DynamicContentType.Summary, DynamicContentType.Element, DynamicContentType.ScreenSaver, DynamicContentType.Footer]

const linkDef = {
    title: 'Link',
    tooltip: 'Configurable link to either a page, external url, internal url, or media lightbox',
    fields: {
        label: { type: EditorFieldType.String, default: '', tooltip: 'Message to display on link button' },
        icon: { type: EditorFieldType.Icon, default: 'fas fa-paper-plane', tooltip: 'Icon to display on link button' },
        link: {
            type: EditorFieldType.String, default: '',
            tooltip: 'Relative or absolute url for button link',
        },
        pageId: {
            type: EditorFieldType.Enum, default: null, label: 'Page', tooltip: 'Page to navigate to', enum: (data, config) => {
                if (config.app) {
                    return config.app.pages.map((x) => ({ id: x.id, name: x.name }))
                } else if (config.organization) {
                    return config.organization.pages.map((x) => ({ id: x.id, name: x.name }))
                }
            }
        },
        mediaId: { type: EditorFieldType.Media, default: null, label: 'Media', tooltip: 'Media item to display' }
    }

}

const mediaSizeEnum = [
    { id: 3, name: 'Quarter Width' },
    { id: 6, name: 'Half Width' },
    { id: 9, name: 'Three Quarter Width' },
]

const defaultMedia = {
    mediaId: null,
    galleryId: null,
    index: 0,
    thumbnailMediaId: null,
    position: 'right',
    size: 8,
    alignment: 'left',
    orientation: 'portrait',
    fit: 'cover',
    subtitle: null,
    clickable: true
}

const mediaDef = {
    title: 'Media',
    fields: {
        mediaId: { type: EditorFieldType.Media, label: 'Media', default: null, tooltip: "Individual media item to display", primary: true, LLM: true, autoDetectAspectRatio: true },
        galleryId: { type: EditorFieldType.Gallery, label: 'Gallery', default: null, tooltip: "Gallery to display", primary: true },
        index: { type: EditorFieldType.Integer, default: 0, dependsOn: 'galleryId', label: 'Gallery Index', tooltip: "Index of media item within selected gallery" },
        thumbnailMediaId: { type: EditorFieldType.Media, label: 'Thumbnail', default: null, tooltip: "Media item to display as thumbnail" },
        position: { type: EditorFieldType.Enum, label: 'Position', default: 'right', enum: (data, config) => ['top', 'bottom', 'left', 'right'].map((x) => ({ id: x, name: x.toReadable() })), tooltip: 'Which position within the section to display media' },
        size: { type: EditorFieldType.Integer, default: 8, label: 'Size', tooltip: 'How large to display the media item, (may be any of 1-10)', slider: { min: 1, max: 10, step: 1 } },
        alignment: { type: EditorFieldType.Enum, label: 'Alignment', default: 'left', enum: (data, config) => ['left', 'center', 'right'].map((x) => ({ id: x, name: x.toReadable() })), tooltip: 'How to align the media item within the section', onDocLink: (x, y) => 'media-alignment' },
        orientation: {
            type: EditorFieldType.Enum,
            label: 'Orientation',
            default: 'portrait',
            enum: (data, config) => [
                { id: 'portrait', name: 'Portrait (9:16)' },
                { id: 'landscape', name: 'Landscape (16:9)' },
                { id: 'square', name: 'Square (1:1)' },
                { id: 'portrait-4-5', name: 'Portrait (4:5)' },
                { id: 'landscape-5-4', name: 'Landscape (5:4)' },
            ],
            tooltip: 'How the media should be oriented, works with fit to correctly display image'
        },
        fit: { type: EditorFieldType.Enum, label: 'Fit', default: 'cover', enum: (data, config) => ['contain', 'cover'].map((x) => ({ id: x, name: x.toReadable() })), tooltip: 'Whether to display the media item in full or crop it' },
        subtitle: { type: EditorFieldType.String, default: null, label: 'Subtitle', tooltip: 'Subtitle to display below media item' },
        clickable: { type: EditorFieldType.Checkbox, default: true, label: 'Open in Lightbox', tooltip: 'Whether the media item can be viewed in a lightbox' },
        mobilePlacement: { type: EditorFieldType.Enum, defaultExclude: true, default: 0, label: 'Mobile Placement', tooltip: 'Controls the positioning of media on mobile devices', enum: (data, config) => ['before', 'after'].map((x, ix) => ({ id: ix, name: x.toReadable() })) },
        show: {
            type: EditorFieldType.Enum, default: 0, label: 'Show', tooltip: 'Controls whether this media item is shown in the content.', enum: (data, config) => [
                { id: 0, name: 'Always' },
                { id: 1, name: 'Desktop Only' },
                { id: 2, name: 'Mobile Only' },
            ]
        },
        aspectRatio: {
            type: EditorFieldType.Enum, default: null, label: 'Aspect Ratio', tooltip: 'Aspect ratio of the video. Auto-detected when a video is selected — override if needed.', enum: (data, config) => [
                { id: null, name: 'Auto' },
                { id: '16:9', name: '16:9 — Landscape' },
                { id: '4:3', name: '4:3 — Landscape' },
                { id: '1:1', name: '1:1 — Square' },
                { id: '4:5', name: '4:5 — Portrait' },
                { id: '2:3', name: '2:3 — Portrait' },
                { id: '9:16', name: '9:16 — Tall Portrait' },
            ]
        },
        order: { type: EditorFieldType.Integer, default: 0, hidden: true },
        mediaStyle: { type: EditorFieldType.Enum, default: 0, label: 'Media Style', tooltip: 'Style to apply to media item', enum: (data, config) => [{id: 0, name: 'A'}, {id: 1, name: 'B'}, {id: 2, name: 'C'}] },
    }
}

const locationDef = {
    title: 'Locations',
    fields: {
        locationId: { type: EditorFieldType.Location, default: null, label: 'Location' },
    }
}

const choiceDef = {
    title: 'Choice',
    fields: {
        label: { type: EditorFieldType.String, default: '' },
        value: { type: EditorFieldType.String, default: null },
        isDefault: { type: EditorFieldType.Checkbox, default: false, tooltip: 'Whether this choice will be selected by default' },
        order: { type: EditorFieldType.Integer, default: 0, hidden: true },
    }
}
const fieldDef = {
    title: 'Field',
    fields: {
        label: { type: EditorFieldType.String, default: '' },
        formFieldTypeId: {
            type: EditorFieldType.Enum, default: FormFieldType.Text, label: 'Type', enum: (data, config) => {
                return Object.keys(FormFieldType).filter((x) => isNaN(x)).map((x) => ({ id: FormFieldType[x], name: x.toReadable() }))
            }
        },
        body: { type: EditorFieldType.String, default: '', label: 'Description' },
        link: { type: EditorFieldType.String, default: '', tooltip: 'Internal identifier for field', required: true },
        required: { type: EditorFieldType.Checkbox, default: false, tooltip: 'Whether this field is required before submission' },
        isHubspot: { type: EditorFieldType.Checkbox, default: false, tooltip: 'Whether this field will be sent to Hubspot (if configured)' },
        size: { type: EditorFieldType.Integer, default: 1, slider: { min: 1, max: 8, step: 1 }, tooltip: 'How many columns this field should take up (1-8)' },
        breakAfter: { type: EditorFieldType.Checkbox, default: false, tooltip: 'Whether this field should cause the next field to start on a new line' },
        choices: { type: EditorFieldType.Multi, default: [], def: choiceDef, dependsOn: 'formFieldTypeId', dependsOnValue: FormFieldType.Select, reorder: true, tooltip: 'List of choices for select fields' },
        placeholder: { type: EditorFieldType.String, default: '', tooltip: 'Placeholder text for field' },
        defaultValue: { type: EditorFieldType.String, default: '', tooltip: 'Default value for field' },
        adminOnly: { type: EditorFieldType.Checkbox, default: false, adminOnly: true, tooltip: 'Whether this field should only be visible to administrators' },
        order: { type: EditorFieldType.Integer, default: 0, hidden: true },
    }
}
const formDef = {
    title: 'Form',
    fields: {
        analyticsEventTypeId: {
            type: EditorFieldType.Enum, default: CustomEventType.Contact, label: 'Type', enum: (data, config) => {
                return [AnalyticsEvent.RequestInformation, AnalyticsEvent.EventRegistration, AnalyticsEvent.ProjectRegistration].map((x) => ({ id: x, name: AnalyticsEvent[x]?.toReadable() }))
            }
        },
        submitMessage: { type: EditorFieldType.String, default: 'Submit', tooltip: 'Text to display on the submit button' },
        successMessage: { type: EditorFieldType.TextField, default: 'Thank you for your submission!', tooltip: 'Message to display after successful submission' },
        tags: { type: EditorFieldType.String, default: '' },
        formId: { type: EditorFieldType.String, default: '', tooltip: 'Optional form identifier for analytics tracking' },
        alignment: {
            type: EditorFieldType.Enum, label: 'Alignment', default: 0, enum: (data, config) => ['left', 'center', 'right'].map((x, ix) => ({ id: ix, name: x.toReadable() })), tooltip: 'How to align the media item within the section',
            onDocLink: (x, y) => {
                return `form-alignment`
            },
        },
        fields: { type: EditorFieldType.Multi, default: defaultFormFields, def: fieldDef, reorder: true },
        submitToHubspot: { type: EditorFieldType.Checkbox, default: false, tooltip: 'Whether to submit this form to Hubspot' },
        hubspotFormId: { type: EditorFieldType.String, default: null, dependsOn: 'submitToHubspot', tooltip: 'Internal hubspot form ID to submit to' },
        hubspotUseEmbedded: { type: EditorFieldType.Checkbox, default: false, dependsOn: 'hubspotFormId', tooltip: 'Whether to use the embedded hubspot form instead of the API' },
        preventNavigation: { type: EditorFieldType.Checkbox, default: false, tooltip: 'Whether to prevent navigation away from the form until it is submitted. This can be used to ensure users fill out the form before leaving.' },
    }
}

const pointDef = {
    title: 'Point',
    fields: {
        label: { type: EditorFieldType.String, default: '' },
        body: { type: EditorFieldType.TextField, default: '' },
        size: { type: EditorFieldType.Integer, default: 1 },
        x: { type: EditorFieldType.Float, default: 0 },
        y: { type: EditorFieldType.Float, default: 0 },
    }
}

const contentDef = {
    title: 'Content',
    noDelete: true,
    fields: {
        dynamicContentTypeId: {
            label: "Content Type",
            type: EditorFieldType.Enum, default: DynamicContentType.Blurb, enum: (data, config) => Object.keys(DynamicContentType).filter((x) => isNaN(x) && !excludeContentTypes.includes(x)).map((x) => {
                return { id: DynamicContentType[x], name: x?.toReadable() }
            }),
            tooltip: 'The format to display the content, refer to the documentation for a detailed description of each type',
            onDocLink: (x, y) => {
                return `${x}-${Object.keys(DynamicContentType).find(key => DynamicContentType[key] === y.dynamicContentTypeId)?.toLowerCase() || ''}`
            }
        },
        draft: { type: EditorFieldType.Checkbox, default: false, tooltip: 'Whether this content is in draft and should be hidden from non-administrators' },
        title: { type: EditorFieldType.String, default: "Title", LLM: true, tooltip: 'Title to display on the section' },
        subtitle: { type: EditorFieldType.String, default: "Subtitle", LLM: true, tooltip: 'Subtitle to display on the section' },
        header: { type: EditorFieldType.String, default: "Header", LLM: true, tooltip: 'Header to display on the section' },
        body: { type: EditorFieldType.TextField, default: "Body", LLM: true, tooltip: 'Body to display on the section' },
        alignment: { type: EditorFieldType.Enum, default: 0, enum: (data, config) => ['left', 'center', 'right'].map((x, ix) => ({ id: ix, name: x })), tooltip: 'How to align the text horizontally within the section' },
        locations: { type: EditorFieldType.Multi, default: [], def: locationDef, limit: 1, label: 'Location', locationTypeId: [LocationType.Primary] },
        links: { type: EditorFieldType.Multi, default: [], def: linkDef, limit: 3 },
        media: { type: EditorFieldType.Multi, default: [], def: mediaDef, reorder: true },
        size: { type: EditorFieldType.Enum, default: 0, label: 'Font Size', tooltip: 'Font size', enum: (a, b) => [{ id: 0, name: 'Regular' }, { id: 1, name: 'Large' }] },
        expandable: { type: EditorFieldType.Checkbox, default: false, tooltip: 'Whether this content should be cut off with ability to expand section' },
        anchor: { type: EditorFieldType.String, default: '', tooltip: 'Anchor to scroll to when link containing anchor is clicked' },
        tags: { type: EditorFieldType.StringTag, enum: pageTagEnum, default: '' },
        backgroundColor: { type: EditorFieldType.Color, default: null, tooltip: 'Background color for this content' },
        invertTextColor: { type: EditorFieldType.Checkbox, default: false, tooltip: 'Whether to invert the text color for this content (useful for dark backgrounds)' },
        contentStyle: { type: EditorFieldType.Enum, default: 'default', enum: (data, config) => [{ id: 0, name: 'default' }, { id: 1, name: 'full' }], tooltip: 'Content style controls display format for this content' },
        forms: { type: EditorFieldType.Multi, default: [], def: formDef },
        points: { type: EditorFieldType.Multi, default: [], def: pointDef },
    }
}

const subContentBDef = {
    title: 'Element',
    fields: {
        title: contentDef.fields.title,
        subtitle: contentDef.fields.subtitle,
        header: contentDef.fields.header,
        body: contentDef.fields.body,
        links: contentDef.fields.links,
        media: contentDef.fields.media,
        locations: contentDef.fields.locations,
        points: contentDef.fields.points,
    }
}
const subContentDef = {
    title: 'Element',
    fields: {
        dynamicContentTypeId: { ...contentDef.fields.dynamicContentTypeId, default: DynamicContentType.Element, primary: false },
        title: { ...contentDef.fields.title, primary: true },
        subtitle: contentDef.fields.subtitle,
        header: contentDef.fields.header,
        body: contentDef.fields.body,
        alignment: contentDef.fields.alignment,
        links: contentDef.fields.links,
        media: contentDef.fields.media,
        locations: contentDef.fields.locations,
        forms: contentDef.fields.forms,
        points: contentDef.fields.points,
        elements: { type: EditorFieldType.Multi, default: [], def: subContentBDef, reorder: true },
    }
}

contentDef.fields.elements = { type: EditorFieldType.Multi, default: [], def: subContentDef, reorder: true }

const contentRootDef = {
    title: 'Custom Content',
    fields: {
        content: { type: EditorFieldType.Array, default: [], def: contentDef },
    }
}

export const pageDef = {
    title: 'Page',
    fields: {
        name: { type: EditorFieldType.String, default: '' },
        title: { type: EditorFieldType.String, default: '' },
        link: {
            type: EditorFieldType.String,
            default: '',
            validate: (value) => {
                const formatRegex = /^[a-z0-9]+(?:\-[a-z0-9]+)*$/
                if (!formatRegex.test(value)) {
                    return 'Link must be lowercase and can only contain letters, numbers, and hyphens between words'
                }
                if (value.length > 100) {
                    return 'Link must be 100 characters or less'
                }
                const reservedWords = ['admin', 'api', 'www', 'mail', 'ftp', 'assets', 'static', 'public']
                if (reservedWords.includes(value)) {
                    return 'Link cannot use reserved words'
                }
                return null
            }
        },
        pageTypeId: {
            label: 'Page Type',
            type: EditorFieldType.Enum, default: PageType.Info, enum: (data, config) => {
                const internalSet = new Set(excludePageTypes)
                return Object.values(config.pageTypes).filter((x) => {
                    return !internalSet.has(x.name)
                }).map((x) => {
                    return { id: x.id, name: x.name.toReadable() }
                })
            },
            onDocLink: (field, page, config) => getPageTypeDocumentationLink(field, page, config)
        },
        icon: { type: EditorFieldType.Icon, default: '', tooltip: 'Icon to display next to the page name in menu' },
        draft: { type: EditorFieldType.Checkbox, default: false, tooltip: 'Whether this page is in draft and should be hidden from non-administrators' },
        exclusive: { type: EditorFieldType.Checkbox, default: false, tooltip: 'Whether this page should be exclusive to either users or those with correct marketing links' },
        salescenterOnly: { type: EditorFieldType.Checkbox, default: false, tooltip: 'Whether this page should only be visible in the sales center' },
        showInMenu: { type: EditorFieldType.Checkbox, default: true, tooltip: 'Whether this page should be shown in the menu' },
        // tags: { type: EditorFieldType.String, default: '', tooltip: 'Tags to help categorize this page' },
        tags: {
            type: EditorFieldType.StringTag, default: '', enum: pageTagEnum, tooltip: 'Tags to help categorize this page'
        },
        backgroundColor: { type: EditorFieldType.Color, default: null },
        template: { type: EditorFieldType.Checkbox, label: 'Use as Template', default: false, tooltip: 'Mark this page as a template to include in template library.' },
        templateDescription: { type: EditorFieldType.TextField, default: '', tooltip: 'Description explaining what this template is for and when to use it', dependsOn: 'template', dependsOnValue: true },
        helper: { type: EditorFieldType.Checkbox, default: false, tooltip: 'Whether this page should trigger a helper popup' },
        helperText: { type: EditorFieldType.String, default: '', tooltip: 'Text to display in the helper popup' },
        seoDescription: { type: EditorFieldType.TextField, default: '', label: 'SEO Description', tooltip: 'Meta description for search results (max 160 characters)', },
        seoKeywords: { type: EditorFieldType.TextField, default: '', label: 'SEO Keywords', tooltip: 'Comma-separated keywords for this page (optional)' },
        seoImageId: { type: EditorFieldType.Media, default: null, label: 'SEO Image', tooltip: 'Featured image for social sharing and search results (recommended: 1200x630px)' },
        schemaType: { type: EditorFieldType.Enum, default: 'WebPage', label: 'Schema Type', enum: (data, config) => [
            { id: 'WebPage', name: 'Web Page' },
            { id: 'ContactPage', name: 'Contact Page' },
            { id: 'AboutPage', name: 'About Page' },
            { id: 'BlogPosting', name: 'Blog Post' },
            { id: 'LocalBusiness', name: 'Local Business' },
            { id: 'Product', name: 'Product/Property' }
        ], tooltip: 'Schema.org type for enhanced search results' },
        breadcrumbTitle: { type: EditorFieldType.String, default: '', label: 'Breadcrumb Title', tooltip: 'Custom title for breadcrumb navigation (if different from page title)' },
        canonicalUrl: { type: EditorFieldType.String, default: '', label: 'Canonical URL', tooltip: 'Canonical URL if this is a duplicate or similar page' },
    }
}

const pagesRootDef = {
    title: 'Pages',
    fields: {
        pages: { type: EditorFieldType.Array, default: [], def: pageDef },
    }
}

const blogDef = fnc.copyObj(pageDef)
blogDef.title = "Blog"
blogDef.fields.pageTypeId.default = PageType.Blog
blogDef.fields.showInMenu.hidden = true
blogDef.fields.helper.hidden = true
blogDef.fields.helperText.hidden = true
blogDef.fields.datePublished = { type: EditorFieldType.Date, default: new Date(), label: 'Date Published' }
const blogRootDef = fnc.copyObj(pagesRootDef)
blogRootDef.fields.pages.def = blogDef

const globalFields = ['dynamicContentTypeId', 'draft', 'anchor', 'invertTextColor']

const fieldOptions = {
    [DynamicContentType.Splash]: {
        content: {
            include: ['title', 'subtitle', 'body', 'links', 'elements', 'media', 'alignment', 'backgroundColor', 'contentStyle']
        }
    },
    [DynamicContentType.Latest]: {
        content: {
            include: ['title', 'body', 'links', 'alignment']
        }
    },
    [DynamicContentType.ProjectInfo]: {
        content: {
            include: ['title', 'body', 'links', 'media', 'locations', 'alignment', 'header', 'hideInfoSummary'],
            media: {
                include: ['mediaId', 'fit', 'subtitle']
            },
            links: {
                link: { default: '#register' }
            }
        },
        hideInfoSummary: { type: EditorFieldType.Checkbox, default: false, label: 'Hide Info Summary', tooltip: 'Whether to hide the project details summary table (units, square footage, bedrooms, bathrooms)' },
    },
    [DynamicContentType.Event]: {
        content: {
            include: ['title', 'subtitle', 'body', 'elements', 'media', 'alignment'],
            elements: {
                include: ['title', 'body', 'subtitle', 'locations'],
            }
        }
    },
    [DynamicContentType.Footer]: {
        content: {
            include: ['title', 'subtitle', 'body', 'elements', 'media', 'alignment', 'links'],
            elements: {
                include: ['title', 'body', 'subtitle', 'locations', 'links', 'forms'],
            }
        }
    },
    [DynamicContentType.Blurb]: {
        content: {
            include: ['title', 'subtitle', 'body', 'media', 'links', 'alignment', 'contentStyle', 'backgroundColor'],
            media: {
                mobilePlacement: { defaultExclude: false }
            }
        }
    },
    [DynamicContentType.Map]: {
        content: {
            include: ['title', 'contentStyle'],
        },
        contentStyle: { type: EditorFieldType.Enum, enum: (data, config) => [{ id: 0, name: 'All Projects' }, { id: 1, name: 'Relevant Project' }] }
    },
    [DynamicContentType.Accordian]: {
        content: {
            include: ['title', 'subtitle', 'body', 'elements', 'media', 'alignment', 'contentStyle', 'expandable'],
            expandable: { type: EditorFieldType.Checkbox, default: false, label: "Use Pluses", tooltip: "Whether to use plus icons for expand/collapse instead of arrows" },
            elements: {
                include: ['title', 'body', 'media', 'links'],
                media: {
                    include: ['mediaId', 'galleryId', 'fit', 'subtitle'],
                }
            }
        }
    },
    [DynamicContentType.Items]: {
        content: {
            include: ['title', 'elements', 'draft', 'alignment', 'anchor', 'size', 'expandable'],
            size: { type: EditorFieldType.Integer, slider: { min: 2, max: 6, step: 1 }, label: 'Items per Row', tooltip: 'How many items aare visible on each row in desktop view', default: 3 },
            elements: {
                include: ['title', 'media', 'body', 'links'],
                media: {
                    include: ['mediaId', 'galleryId', 'fit', 'subtitle'],
                }
            }
        }
    },
    [DynamicContentType.RecentBlog]: {
        content: {
            include: ['title', 'body', 'elements', 'alignment', 'tags', 'contentStyle', 'size', 'expandable'],
            size: { type: EditorFieldType.Integer, slider: { min: 1, max: 2, step: 1 }, label: 'Rows to Show', tooltip: 'Rows to Show', default: 1 },
            elements: {
                include: ['title', 'media', 'body', 'links'],
                media: {
                    include: ['mediaId', 'galleryId', 'fit', 'subtitle'],
                }
            },
            contentStyle: { enum: (data, config) => [{ id: 0, name: 'Vertical' }, { id: 1, name: 'Horizontal' }, { id: 2, name: 'Feature with Column' }] }
        }
    },
    [DynamicContentType.Header]: {
        content: {
            include: ['media', 'draft', 'title', 'alignment'],
            media: {
                include: ['mediaId', 'galleryId', 'subtitle'],
                galleryId: { primary: true }
            },
        }
    },
    [DynamicContentType.Form]: {
        content: {
            include: ['title', 'subtitle', 'body', 'forms', 'alignment'],
        }
    },
    [DynamicContentType.Quotes]: {
        content: {
            include: ['elements', 'draft'],
            elements: {
                label: 'Quote',
                include: ['title', 'body', 'subtitle', 'header', 'media'],
                title: { label: 'Name' },
                body: { label: 'Description' },
                subtitle: { label: 'Organization' },
                header: { label: 'Quote' },
            }
        }
    },
    [DynamicContentType.Testimonials]: {
        content: {
            include: ['elements', 'size', 'draft'],
            size: {
                type: EditorFieldType.Integer,
                slider: { min: 2, max: 15, step: 1 },
                label: 'Auto Scroll (seconds)',
                tooltip: 'Time between testimonial slides',
                default: 6,
            },
            elements: {
                label: 'Testimonial',
                include: ['title', 'subtitle', 'body', 'footer', 'media'],
                title: { label: 'Name' },
                subtitle: { label: 'Organization' },
                body: { label: 'Quote' },
                footer: { type: EditorFieldType.TextField, label: 'Credentials (Markdown)', default: '' },
            }
        }
    },
    [DynamicContentType.ScreenSaver]: {
        content: {
            include: ['media', 'draft'],
            media: {
                include: ['galleryId', 'subtitle'],
            }
        }
    },
    [DynamicContentType.Points]: {
        content: {
            include: ['title', 'media', 'elements', 'size', 'draft'],
            elements: {
                include: ['title', 'body', 'media', 'points'],
                media: {
                    include: ['mediaId', 'galleryId', 'fit', 'subtitle'],
                },
                points: {
                    include: ['x', 'y'],
                    x: { primary: true },
                    y: { primary: true },
                }
            }
        }
    },
    [DynamicContentType.Neighbourhood]: {
        content: {
            include: ['title', 'media', 'elements'],
            elements: {
                include: ['title', 'elements'],
                elements: {
                    include: ['title', 'locations'],
                    locations: {
                        include: ['locationId'],
                        locationId: { locationTypeId: [LocationType.Primary, LocationType.Neighbourhood] }
                    }
                }
            }
        }
    },
    [DynamicContentType.Numbered]: {
        content: {
            include: ['title', 'subtitle', 'body', 'elements', 'links', 'alignment'],
            elements: {
                include: ['title', 'subtitle', 'body', 'links', 'media'],
                media: {
                    include: ['mediaId', 'galleryId', 'fit', 'subtitle'],
                }
            }
        }
    },
    [DynamicContentType.Developers]: {
        content: {
            include: ['title', 'subtitle', 'body', 'alignment'],
        }
    },
    [DynamicContentType.Steps]: {
        content: {
            include: ['title', 'subtitle', 'body', 'elements', 'links', 'alignment'],
            elements: {
                include: ['title', 'subtitle', 'body', 'links', 'media'],
                media: {
                    include: ['mediaId', 'galleryId', 'fit', 'subtitle'],
                }
            }
        }
    },
    [DynamicContentType.Summary]: {
        content: {
            include: ['title', 'subtitle', 'body', 'links', 'alignment'],
        }
    },
    [DynamicContentType.Tabbed]: {
        content: {
            include: ['elements'],
            elements: {
                include: ['title', 'subtitle', 'body', 'links', 'media'],
                media: {
                    include: ['mediaId', 'galleryId', 'fit', 'subtitle'],
                }
            }
        }
    },
    [DynamicContentType.Grid]: {
        content: {
            include: ['title', 'subtitle', 'body', 'links', 'elements', 'size', 'media', 'expandable', 'alignment'],
            media: {
                include: ['galleryId', 'mediaId', 'mediaStyle', 'subtitle'],
            },
            elements: {
                include: ['title', 'subtitle', 'body', 'links', 'media', 'size'],
                media: {
                    include: ['mediaId', 'galleryId', 'fit', 'mediaStyle', 'subtitle'],
                }
            },
            size: { type: EditorFieldType.Integer, default: 1, label: 'Columns', tooltip: 'Number of horizonal columns for the grid (2-6)', slider: { min: 2, max: 6, step: 1 }, enum: null }
        }
    },
    [DynamicContentType.Features]: {
        content: {
            include: ['elements'],
            elements: {
                include: ['title', 'subtitle', 'body', 'links', 'media', 'size'],
            }
        }
    },
    [DynamicContentType.Latest]: {
        content: {
            include: ['title', 'subtitle', 'body', 'alignment'],
        }
    },
    [DynamicContentType.Search]: {
        content: {
            include: ['title', 'subtitle', 'body', 'alignment', 'contentStyle', 'draft'],
        }
    },
    [DynamicContentType.RequestInfo]: {
        content: {
            include: [],
        }
    },
    [DynamicContentType.Media]: {
        content: {
            include: ['media', 'draft'],
        }
    },
    [DynamicContentType.ImageCarousel]: {
        content: {
            include: ['title', 'subtitle', 'body', 'media', 'size', 'draft', 'alignment'],
            size: {
                type: EditorFieldType.Integer,
                slider: { min: 1, max: 10, step: 1 },
                label: 'Speed (seconds)',
                tooltip: 'How fast the carousel scrolls in seconds (1-10 seconds)',
                default: 3
            }
        }
    },
    [DynamicContentType.Tour]: {
        content: {
            include: ['title', 'subtitle', 'body', 'media', 'links', 'alignment', 'draft', 'tags'],
            media: {
                include: ['mediaId', 'galleryId', 'subtitle'],
            },
            tags: {
                type: EditorFieldType.StringTag,
                default: '',
                tooltip: 'Filter virtual tours by tag. Leave empty to show all tours. Separate multiple tags with commas.',
                enum: (data, config) => {
                    // Get available modelhome tags from app or organization
                    const source = config.app || config.organization
                    const modelhomes = source?.modelhomes || []
                    const tagSet = new Set(modelhomes.map((x) => x.tag).filter((x) => x && x.length > 0))
                    return Array.from(tagSet).sort()
                }
            }
        }
    },
}

function getDef(type) {
    if (!(type in fieldOptions)) {
        return fnc.copyObj(contentRootDef)
    }
    const newOptions = { ...fieldOptions[type] }
    const filterDef = (subDef, options) => {
        if (!options) {
            return subDef
        }
        const filteredDef = { ...subDef, fields: { ...subDef.fields } }

        const overridables = ['type', 'default', 'label', 'primary', 'enum', 'locationTypeId', 'slider', 'tooltip', 'defaultExclude']
        Object.keys(filteredDef.fields).forEach((key) => {
            overridables.forEach((overridable) => {
                if (options[key] && overridable in options[key] &&
                    (Array.isArray(options[key][overridable]) || typeof options[key][overridable] !== 'object' || options[key][overridable] === null || overridable == 'slider')) {
                    filteredDef.fields[key][overridable] = options[key][overridable]
                }
            })
        })

        Object.keys(filteredDef.fields).forEach((key) => {
            if (options.include && !options.include.includes(key) && !globalFields.includes(key)) {
                delete filteredDef.fields[key]
            }

            if (((options.exclude && options.exclude.includes(key)) || filteredDef.fields[key]?.defaultExclude)
                && !globalFields.includes(key)) {
                delete filteredDef.fields[key]
            }
        })

        Object.keys(filteredDef.fields).forEach((key) => {
            if (filteredDef.fields[key].def != null && key in options) {
                filteredDef.fields[key].def = filterDef(filteredDef.fields[key].def, options[key])
                if (key in options && options[key].label) {
                    filteredDef.fields[key].def.title = options[key].label
                }
            }
        })
        return filteredDef
    }
    const newDef = filterDef(fnc.copyObj(contentRootDef), newOptions)
    return newDef
}

const defaultPage = {
    id: null,
    title: '',
    name: '',
    link: '',
    icon: 'fas fa-plus',
    draft: true,
    exclusive: false,
    showInMenu: false,
    salesCenterOnly: false,
    pageType: PageType.Custom,
    datePublished: fnc.formatDateToYYYYMMDD(new Date())
}

const defaultContent = {
    dynamicContentTypeId: DynamicContentType.Blurb,
    title: null,
    subtitle: null,
    body: null,
    elements: [],
    links: [],
    media: [],
    locations: [],
    contentStyle: 0,
    forms: [],
    points: [],
    alignment: 0,
}

interface InlineContentEditorProps {
    nested: boolean
}
export function InlineContentEditor(props: InlineContentEditorProps) {
    const { nested } = props
    const dispatch = useAppDispatch()

    const adminApp = useAppSelector((state) => state.admin.project)
    const adminOrg = useAppSelector((state) => state.admin.organization)
    const editContent = useAppSelector((state) => state.admin.editContent)
    const editInline = useAppSelector((state) => state.admin.editInline)
    const app = useAppSelector((state) => state.app.projectCurrent[0])
    const organization = useAppSelector((state) => state.app.organization)
    const config = useAppSelector((state) => state.app.config)
    const prompt = useAppSelector((state) => state.app.prompt)
    const allMedia = useAppSelector((state) => state.app.media)
    const links = useAppSelector((state) => state.app.links)
    const initialized = useAppSelector((state) => state.app.initialized)
    const standaloneOrg = useAppSelector((state) => state.app.standaloneOrg)
    const standaloneApp = useAppSelector((state) => state.app.standaloneApp)
    const customDomain = useAppSelector((state) => state.app.customDomain)

    const page = usePage()
    const hybridContent = useHybridContent()
    const downloadMedia = useDownloadMedia(app, organization)

    const appStaticContent = projectStaticContent(app)
    const orgStaticContent = !app ? organizationStaticContent(organization) : null
    const orgPermissions = useAdminPermissions(null, organization, [UserRole.OrganizationsEdit])

    const [data, setData] = useState(null)
    const [expanded, setExpanded] = useState(false)
    const [pendingChange, setPendingChange] = useState(false)
    const [elem, setElem] = useState(null)
    const [LLMHelper, setLLMHelper] = useState(null)
    const [show, setShow] = useState(null)
    const [saving, setSaving] = useState(false)
    const savingRef = useRef(false)
    const suppressChangeUntilRef = useRef(0)
    const [saveText, setSaveText] = useState('Save')
    const [focusContent, setFocusContent] = useState(null)
    const [pageContent, setPageContent] = useState(null)
    const [footerContent, setFooterContent] = useState(null)

    // Tracks whether a reorder operation is in progress.
    // Blocks pageContent re-derivation from hybridContent which would re-normalize orders.
    const reorderInProgress = useRef(false)
    const reorderTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

    // Reorder managers - single source of truth for order state
    const pageReorderManager = useRef<ReorderManager | null>(null)
    const footerReorderManager = useRef<ReorderManager | null>(null)

    const getPageReorderManager = () => {
        if (!pageReorderManager.current) {
            pageReorderManager.current = new ReorderManager({
                onOrderChanged: (updatedItems) => {
                    logger.info('[Reorder] onOrderChanged:', updatedItems.map(u => `${u.id?.substring(0, 8)}→order:${u.order}`).join(', '))
                    markReorderInProgress()
                    // Optimistically update Redux so DynamicContentView and all other consumers see new orders instantly
                    dispatch(optimisticReorder({ isApp: !!app, items: updatedItems.map(u => ({ id: u.id, order: u.order })) }))
                    setPageContent((prev) => {
                        if (!prev) return prev
                        const updated = prev.map((item) => {
                            const change = updatedItems.find(u => u.id === item.id)
                            return change ? { ...item, order: change.order } : item
                        })
                        logger.info('[Reorder] pageContent after apply:', updated.map(x => `${x.id?.substring(0, 8)}:${x.order}`).join(', '))
                        return updated
                    })
                },
                saveFn: (items) => handleReorderSave(items),
            })
        }
        return pageReorderManager.current
    }

    const getFooterReorderManager = () => {
        if (!footerReorderManager.current) {
            footerReorderManager.current = new ReorderManager({
                onOrderChanged: (updatedItems) => {
                    logger.info('[Reorder] footer onOrderChanged:', updatedItems.map(u => `${u.id?.substring(0, 8)}→order:${u.order}`).join(', '))
                    markReorderInProgress()
                    dispatch(optimisticReorder({ isApp: !!app, items: updatedItems.map(u => ({ id: u.id, order: u.order })) }))
                    setFooterContent((prev) => {
                        if (!prev) return prev
                        const updated = prev.map((item) => {
                            const change = updatedItems.find(u => u.id === item.id)
                            return change ? { ...item, order: change.order } : item
                        })
                        return updated
                    })
                },
                saveFn: (items) => handleReorderSave(items),
            })
        }
        return footerReorderManager.current
    }
    const [listElem, setListElem] = useState(null)
    const [editPage, setEditPage] = useState(null)
    const [currentAppId, setCurrentAppId] = useState(null)
    const [currentOrgId, setCurrentOrgId] = useState(null)
    const [generatingPage, setGeneratingPage] = useState(false)
    const [templateMode, setTemplateMode] = useState(false)
    const [cloneMode, setCloneMode] = useState(false)
    const [cloneData, setCloneData] = useState(null) // Local state for clone template data

    const [tab, setTab] = nested ? useState(EditorTab.Content) : useLocalStorage('editor-tab', EditorTab.Content)

    const scrollContentId = useRef()
    const contentRef = useRef()
    const mousePositionRef = useRef()
    const currentPage = useRef()
    const validateRef = useRef()
    const linkEditTimeout = useRef()
    const lastEditContentRef = useRef()
    const lastEditPageRef = useRef()

    let pageTypeMap = {}
    let pages = app ? [...app.pages] : organization ? [...organization.pages] : []
    let posts = pages.filter((x) => x.pageTypeId == PageTypeId.Blog)
    pages = pages.filter((x) => {
        return x.pageTypeId != PageTypeId.Blog
    })

    let staticPages = []
    if (!app && pages) {
        const orgPages = organizationRoutes(organization).filter((x) => x.pageTypeId != PageTypeId.Blog)
        const staticKeys = new Set(orgPages.map((x) => x.link))
        const dynamicKeys = new Set(pages.map((x) => x.link))
        const onlyStaticKeys = staticKeys.difference(dynamicKeys)
        staticPages = orgPages.filter((x) => onlyStaticKeys.has(x.link))
        staticPages.forEach((x) => {
            pages.push(x)
        })
    }

    useEffect(() => {
        // Detect if we are missing "home" page and add it
        if (!saving && initialized >= 2 && config && !app && links.appLink == null && organization && pages && !pages.find((x) => x.link.includes('home'))) {
            handleAddHomePage()
        }
    }, [app, organization, page, config, saving, links])

    useEffect(() => {
        // Look for existance of #chat-widget-container and destroy it
        const chatWidget = document.getElementById('chat-widget-container')
        if (chatWidget) {
            logger.info('InlineContentEditor: Removing chat widget')
            chatWidget.remove()
            logger.info('InlineContentEditor: Chat widget removed')
        }
    }, [])

    useEffect(() => {
        if (!organization || (app && app?.meta.id == currentAppId) || links.pageLink == PageType.Admin) {
            return
        }
        dispatch(initializeAdmin({ appLink: app?.meta.link, options: { organization: organization.link, editor: true, theme: false } }))
        if (app) {
            dispatch(initializeAdminProject({ appLink: app.meta.link, organizationLink: organization.link, options: { theme: false } }))
        }
        dispatch(fetchDocumentationLinks())
        setCurrentAppId(app?.meta.id)
        setCurrentOrgId(organization?.id)
    }, [app, organization])

    useEffect(() => {
        if (app && (!adminApp || adminApp.id != app.id)) {
            dispatch(setProject(app))
        } else if (adminApp) {
            dispatch(updateProjectCurrent(adminApp))
        }
    }, [adminApp, app])

    useEffect(() => {
        // Sync admin org with current org
        if (organization && (!adminOrg || adminOrg.id != organization.id)) {
            dispatch(setOrganization(organization))
        } else if (adminOrg && organization && adminOrg.modified != organization.modified) {
            // Skip sync during active reorder — both stores are now updated
            // directly via extraReducers (optimisticReorder + batchUpsertContent.fulfilled),
            // so the sync is unnecessary and can propagate stale data.
            if (reorderInProgress.current) {
                logger.info(`[Reorder] admin→app org sync SKIPPED (reorder in progress) modified ${organization.modified} → ${adminOrg.modified}`)
                return
            }
            // If adminOrg.modified is undefined, the admin org was replaced by an external
            // source (e.g. server refresh) that doesn't have our synthetic modified field.
            // In this case, push the app org (which has correct reorder data from
            // extraReducers) back to admin, rather than letting stale admin data overwrite app.
            if (!adminOrg.modified && organization.modified) {
                logger.info(`[Reorder] admin→app org sync REVERSED (adminOrg.modified is undefined, keeping app state) modified ${organization.modified} → ${adminOrg.modified}`)
                dispatch(setOrganization(organization))
                return
            }
            const orgOrders = adminOrg.dynamicContent?.map(x => `${x.id?.substring(0, 8)}:${x.order}`).join(', ')
            logger.info(`[Reorder] admin→app org sync (modified ${organization.modified} → ${adminOrg.modified}) orders: ${orgOrders}`)
            dispatch(updateOrganization(adminOrg))
            if (prompt && prompt.organization) {
                dispatch(updatePrompt({ organization: adminOrg }))
            }
        }
    }, [adminOrg, organization])

    useEffect(() => {
        // Sync admin app with current app
        if (app && (!adminApp || adminApp.id != app.id)) {
            dispatch(setProject(app))
        } else if (adminApp) {
            logger.info(`[Reorder] admin→app project sync`)
            dispatch(updateProjectCurrent(adminApp))
        }
    }, [adminApp, app])

    useEffect(() => {
        // if (!page) {
        // return
        // }
        if (focusContent && currentPage.current != page?.id) {
            dispatch(clearEditContent())
            setFocusContent(null)
        }

        // Reset reorder managers when page changes
        if (currentPage.current != page?.id) {
            pageReorderManager.current?.reset()
            footerReorderManager.current?.reset()
        }
        currentPage.current = page?.id

        // Skip re-deriving pageContent while reorder operations are in progress.
        // The reorder manager optimistically updates pageContent directly;
        // re-deriving via hybridContent would re-normalize orders and undo the swap.
        if (reorderInProgress.current) {
            logger.info('[Reorder] pageContent derivation skipped - reorder in progress')
            return
        }

        const footerPage = pages?.find((x) => x.pageType == PageType.Footer || x.link == 'footer')
        if (app && page && appStaticContent) {
            const content = hybridContent(app, null, organization, page, appStaticContent)
            setPageContent(content)
            getPageReorderManager().sync(content)
            if (footerPage) {
                const appContent = hybridContent(app, null, organization, footerPage, appStaticContent)
                setFooterContent(appContent)
                getFooterReorderManager().sync(appContent)
            }
        } else if (orgStaticContent) {
            const content = hybridContent(null, null, organization, page, orgStaticContent)
            setPageContent(content)
            getPageReorderManager().sync(content)
            if (footerPage) {
                const appContent = hybridContent(app, null, organization, footerPage, orgStaticContent)
                setFooterContent(appContent)
                getFooterReorderManager().sync(appContent)
            }
        } else if (app) {
            const dbContent = app.dynamicContent?.filter((x) => x.pageId == page?.id)
            setPageContent(dbContent)
            getPageReorderManager().sync(dbContent)
            if (footerPage) {
                const appContent = app.dynamicContent?.filter((x) => x.pageId == footerPage.id)
                setFooterContent(appContent)
                getFooterReorderManager().sync(appContent)
            }
        } else if (organization) {
            const dbContent = organization.dynamicContent?.filter((x) => x.pageId == page?.id)
            setPageContent(dbContent)
            getPageReorderManager().sync(dbContent)
            if (footerPage) {
                const appContent = organization.dynamicContent?.filter((x) => x.pageId == footerPage.id)
                setFooterContent(appContent)
                getFooterReorderManager().sync(appContent)
            }
        }

    }, [app, organization, page])

    useEffect(() => {
        if (tab == EditorTab.Content) {
            // Only update if the content has actually changed, not just the reference
            const newData = { ...defaultContent, ...editContent }
            const currentDataStr = JSON.stringify(data)
            const newDataStr = JSON.stringify(newData)

            if (currentDataStr !== newDataStr) {
                setData(fnc.copyObj(newData))
            }
        } else if (tab == EditorTab.Pages || tab == EditorTab.Blogs) {
            // Always update data when editPage changes, since GenericEditor modifies by reference
            setData(fnc.copyObj(editPage))
        }
    }, [editContent, editPage])

    useEffect(() => {
        if (editInline && !show) {
            setShow(true)
        }
    }, [editInline])

    useEffect(() => {
        window.addEventListener(CustomEventType.ContentAction, handleContentAction)
        return () => {
            window.removeEventListener(CustomEventType.ContentAction, handleContentAction)
        }
    })

    useEffect(() => {
        if (editContent?.dynamicContentTypeId == DynamicContentType.Points) {
            const handleEditPoints = (e) => {
                const { element, point } = e.detail
                const elemIdx = data.elements.findIndex((x) => x.id == element.id)
                if (elemIdx > -1) {
                    const elem = data.elements[elemIdx]
                    const pointIdx = elem.points.findIndex((x) => x.id == point.id)
                    if (pointIdx > -1) {
                        data.elements[elemIdx].points[pointIdx] = point
                        handleChange()
                    }
                }
            }
            window.addEventListener(CustomEventType.EditPoint, handleEditPoints)
            return () => {
                window.removeEventListener(CustomEventType.EditPoint, handleEditPoints)
            }
        }
    }, [editContent, data])

    useEffect(() => {
        if (!listElem) {
            return
        }
        const checkOverlap = (elemA, elemB, e) => {
            if (!mousePositionRef.current) return null
            // Check if the mouse event position is overlapping the bounding box of elemA or elemB 
            const rectA = elemA.getBoundingClientRect()
            const rectB = elemB.getBoundingClientRect()
            const { x, y } = mousePositionRef.current
            if (x >= rectA.left && x <= rectA.right && y >= rectA.top && y <= rectA.bottom) {
                return elemA
            }
            if (x >= rectB.left && x <= rectB.right && y >= rectB.top && y <= rectB.bottom) {
                return elemB
            }
            return null

        }
        const matchContent = (listA, listB, className = '.page-content', offset = 0) => {
            // Get all list elemens
            const elementsA = listA.querySelectorAll(className)
            // Figure out which element is closest to the top
            let closest = null
            elementsA.forEach((x) => {
                if (!closest) {
                    closest = x
                    return
                }
                if (Math.abs(x.offsetTop - listA.scrollTop) < Math.abs(closest.offsetTop - listA.scrollTop)) {
                    closest = x
                }
            })
            if (closest) {
                // Get
                const id = closest.getAttribute('data-id')
                if (id && id != scrollContentId.current) {
                    scrollContentId.current = id
                    // Scroll the content to the same id
                    const targetB = listB.querySelector(`[data-id="${id}"]`)
                    if (targetB) {
                        // Scroll smooth
                        listB.scrollTo({ top: targetB.offsetTop + offset, behavior: 'smooth' })
                    }
                }
            }
        }

        const scrollContent = (e) => {
            if (checkOverlap(contentRef.current, listElem, e) != contentRef.current) {
                return
            }
            matchContent(contentRef.current, listElem, '.dynamic-content', -60)
        }

        const scrollList = (e) => {
            if (!contentRef.current) {
                return
            }
            if (checkOverlap(contentRef.current, listElem) != listElem) {
                return
            }
            matchContent(listElem, contentRef.current, '.page-content', -100)
        }

        const getMousePosition = (e) => {
            mousePositionRef.current = { x: e.clientX, y: e.clientY }
        }

        const interval = setInterval(() => {
            contentRef.current = document.querySelector('.dynamic.scrollable')
            if (contentRef.current) {
                contentRef.current.addEventListener('scroll', scrollContent)
                clearInterval(interval)
                return
            }
        }, 1000)
        listElem.addEventListener('scroll', scrollList)
        window.addEventListener('mousemove', getMousePosition)
        return () => {
            listElem.removeEventListener('scroll', scrollList)
            if (contentRef.current) {
                contentRef.current.removeEventListener('scroll', scrollContent)
            }
            clearInterval(interval)
            window.removeEventListener('mousemove', getMousePosition)
        }
    }, [page, listElem, pageContent])

    function handleContentAction(e) {
        const { action, data, page } = e.detail
        const { id, staticLink, direction } = data
        logger.info(`[Reorder] handleContentAction action=${action} id=${id?.substring(0, 8)} dir=${direction} tab=${tab} items=${pageContent?.length}`)
        let content = pageContent.find((x) => x.id == id || (x.staticLink && x.staticLink == staticLink))
        let isFooter = false
        if (!content) {
            content = footerContent?.find((x) => x.id == id || (x.staticLink && x.staticLink == staticLink))
            isFooter = true
            logger.info(`[Reorder] found in ${isFooter ? 'footer' : 'page'}: ${content?.id?.substring(0, 8)} order=${content?.order}`)
        } else {
            logger.info(`[Reorder] found in page: ${content?.id?.substring(0, 8)} order=${content?.order}`)
        }
        if (!content && action != 'add') {
            logger.error(`[Reorder] content not found: action=${action} id=${id} staticLink=${staticLink}`)
            dispatch(showPrompt({ title: 'Error', message: 'Failed to find content', type: PromptType.Confirm, cancelMessage: null }))
            return
        }
        if (tab != EditorTab.Content) {
            logger.info(`[Reorder] wrong tab (${tab}), switching to Content`)
            setTab(EditorTab.Content)
            return
        }
        setTimeout(() => {
            switch (action) {
                case 'add':
                    handleAddContent(data, -1, page)
                    break
                case 'edit':
                    if (!content.id || (content.staticLink && content.staticLink == content.id)) {
                        handleAddContent(content, -1, page)
                    } else {
                        dispatch(setEditContent(fnc.copyObj(content)))
                    }
                    break
                case 'order':
                    handleOrder(content, direction, isFooter)
                    break
                case 'delete':
                    handleDelete(content)
                    break
                case 'cancel':
                    handleCancel()
                    break
                case 'copy':
                    handleCopy(content)
                    break
                default:
                    break
            }
        }, 0)
    }

    function handleSave(content, validate = false, _tab = tab) {
        logger.info('[Reorder] handleSave - Save called:', {
            isArray: Array.isArray(content),
            itemCount: Array.isArray(content) ? content.length : 1,
            tab: _tab,
            validate,
            items: Array.isArray(content)
                ? content.map(x => ({ id: x.id, order: x.order, title: x.title, pageId: x.pageId }))
                : { id: content.id, order: content.order, title: content.title, pageId: content.pageId },
        })
        if (validateRef.current && (!Array.isArray(content) || content.length == 1)) {
            const validation = validateRef.current()
            if (validation && validation.length > 0) {
                let validationMessage = "Please solve the following issues before saving:"
                validationMessage += "\n\n" + validation.map((x) => "* " + x).join("\n\n")
                dispatch(showPrompt({ type: PromptType.Confirm, title: 'Validation Error', message: validationMessage, cancelMessage: null }))
                return
            }
        }
        let proms = []
        if (_tab == EditorTab.Content) {
            if (Array.isArray(content)) {
                logger.info('[Reorder] handleSave - Dispatching batch upsertContent:', {
                    count: content.length,
                    items: content.map(x => ({ id: x.id, order: x.order })),
                })
                proms = content.map((x) => dispatch(upsertContent({ app, organization, data: x })))
            } else {
                proms = [dispatch(upsertContent({ app, organization, data: content }))]
            }
        } else if (_tab == EditorTab.Pages || _tab == EditorTab.Blogs) {
            if (Array.isArray(content)) {
                proms = content.map((x) => dispatch(upsertPage({ app, organization, page: x })))
            } else {
                proms = [dispatch(upsertPage({ app, organization, page: content }))]
            }
        }

        suppressChangeUntilRef.current = Date.now() + 2000
        setSaveText('Saving')
        setSaving(true)
        savingRef.current = true
        return Promise.all(proms)
            .then((x) => {
                logger.info('[Reorder] handleSave - Save results:', {
                    resultCount: x.length,
                    results: x.map(y => ({
                        status: y?.meta?.requestStatus || y?.status,
                        payload: y?.payload ? { id: y.payload.id, order: y.payload.order } : null,
                        error: y?.error || null,
                    })),
                })
                setTimeout(() => {
                    savingRef.current = false
                    setSaving(false)
                    const failed = x.filter((y) => y?.meta?.requestStatus === 'rejected' || y?.status === 'error')
                    if (failed.length > 0) {
                        logger.error('[Reorder] handleSave - Save had errors:', failed)
                        const errorDetails = failed.map((y) => {
                            const msg = y?.payload?.message || y?.error?.message || y?.payload || 'Unknown error'
                            return typeof msg === 'string' ? msg : JSON.stringify(msg)
                        }).join('\n')
                        setSaveText('Error')
                        dispatch(showPrompt({
                            type: PromptType.Confirm,
                            title: 'Save Failed',
                            message: `Your changes could not be saved. Please share this error with your administrator:\n\n${errorDetails}`,
                            cancelMessage: null,
                        }))
                    } else {
                        suppressChangeUntilRef.current = Date.now() + 1500
                        setPendingChange(false)
                        setSaveText('Saved!')
                    }
                }, 10)
                return x.map((y) => {
                    if (Array.isArray(y.payload)) {
                        return y.payload[0]
                    } else {
                        return y.payload
                    }
                })
            })
            .then((savedResults) => {
                if (_tab == EditorTab.Pages || _tab == EditorTab.Blogs) {
                    // Check if we're editing the current page and the link changed
                    const savedPage = savedResults[0]?.page // Get the first saved page from results
                    handleGoToContent(savedPage)
                }
                return savedResults
            })
            .catch((e) => {
                savingRef.current = false
                setSaving(false)
                setSaveText('Error')
                logger.error('[InlineContentEditor] handleSave failed:', e)
            })
            .finally(() => {
                // Safety net: ensure saving state is always cleared
                if (savingRef.current) {
                    savingRef.current = false
                    setSaving(false)
                    setSaveText('Save')
                }
            })
    }

    function handleCancel() {
        const close = () => {
            setPendingChange(false)
            if (editPage) {
                setEditPage(null)
            } else {
                dispatch(clearEditContent())
            }
        }
        if (pendingChange) {
            dispatch(showPrompt(PromptOptions.UnsavedChanges))
                .then((x) => {
                    if (x.payload) {
                        close()
                    }
                })
        } else {
            close()
        }
    }

    function handleClose() {
        const close = () => {
            setShow(false)
            setTimeout(() => {
                let elements = document.querySelectorAll('.dynamic-content.visible')
                const ids = new Set()
                elements.forEach((x) => {
                    ids.add(x.getAttribute('id'))
                })
                dispatch(setEditInline(false))
                setShow(null)
                setData(null)

                setTimeout(() => {
                    elements = document.querySelectorAll('.dynamic-content')
                    elements?.forEach((x) => {
                        const id = x.getAttribute('id')
                        if (ids.has(id)) {
                            x.classList.add('visible')
                        }
                    })
                }, 0)
            }, 100)
            setPendingChange(false)
        }
        if (pendingChange) {
            dispatch(showPrompt(PromptOptions.UnsavedChanges))
                .then((x) => {
                    if (x.payload) {
                        close()
                    }
                })
        } else {
            close()
        }
    }

    function handleDelete(content) {
        if (tab == EditorTab.Content) {
            dispatch(showPrompt({ type: PromptType.Confirm, title: 'Delete Content', message: 'Are you sure you want to delete this content?', confirmMessage: 'Delete' }))
                .then((x) => {
                    if (x.payload) {
                        if (editContent?.id == content.id) {
                            handleCancel()
                        }
                        dispatch(removeContent({ app, organization, data: content }))
                    }
                })
        } else if (tab == EditorTab.Pages) {
            dispatch(showPrompt({ type: PromptType.Confirm, title: 'Delete Page', message: 'Are you sure you want to delete this page?', confirmMessage: 'Delete', confirmMatch: `delete ${content.link}`, confirmMatchWarning: 'Final warning, this cannot be undone' }))
                .then((x) => {
                    if (x.payload) {
                        if (editPage?.id == content.id) {
                            handleCancel()
                        }
                        dispatch(removePage({ app, organization, page: content }))
                    }
                })
        } else if (tab == EditorTab.Blogs) {
            dispatch(showPrompt({ type: PromptType.Confirm, title: 'Delete Blog Post', message: 'Are you sure you want to delete this blog post?', confirmMessage: 'Delete', confirmMatch: `delete ${content.link}`, confirmMatchWarning: 'Final warning, this cannot be undone' }))
                .then((x) => {
                    if (x.payload) {
                        if (editPage?.id == content.id) {
                            handleCancel()
                        }
                        dispatch(removePage({ app, organization, page: content }))
                    }
                })
        }
    }

    /**
     * Consolidated navigation handler for all page navigation within the editor.
     * Handles: standaloneOrg, standaloneApp, customDomain, and retains org+app in URL.
     * 
     * @param x - The page object to navigate to (must have a `link` property)
     * @param options - Optional navigation options
     *   - targetApp: explicit target app for cross-app navigation
     *   - targetOrganization: explicit target organization
     *   - reload: force page reload after navigation
     */
    function handleGoToContent(x, options: { targetApp?: any, targetOrganization?: any, reload?: boolean } = {}) {
        if (!x || !x.link) {
            logger.warn('handleGoToContent: No valid page or link provided', x)
            return
        }

        const { targetApp = null, targetOrganization = null, reload = false } = options
        const currentPath = window.location.pathname

        // Determine target context - use provided targets or fall back to current context
        const effectiveOrg = targetOrganization || organization
        const effectiveApp = targetApp || app

        // Get links for org and app
        const orgLink = effectiveOrg?.link
        const appLink = effectiveApp?.meta?.link || effectiveApp?.link

        // Check if we're navigating to a different context (requires reload)
        const pageAppId = x.appId || targetApp?.meta?.id || targetApp?.id
        const pageOrgId = x.organizationId || targetOrganization?.id
        const currentAppId = app?.meta?.id || app?.id
        const currentOrgId = organization?.id
        const isDifferentApp = pageAppId && currentAppId && pageAppId !== currentAppId
        const isDifferentOrg = pageOrgId && currentOrgId && pageOrgId !== currentOrgId

        let finalLink = ''

        // Handle custom domain scenarios first
        if (customDomain?.app) {
            // Custom app domain (e.g., myapp.com) - don't include org or app in URL
            finalLink = `/${x.link}`
        } else if (customDomain?.organization) {
            // Custom org domain (e.g., myorg.com) - don't include org, but include app if present
            if (appLink && currentPath.includes(`/${appLink}/`)) {
                finalLink = `/${appLink}/${x.link}`
            } else if (appLink && pageAppId) {
                // Navigating to an app page
                finalLink = `/${appLink}/${x.link}`
            } else {
                finalLink = `/${x.link}`
            }
        } else {
            // Standard URL construction - check current path to determine what to include
            // This preserves the current URL structure (org, org+app, or just app)
            
            const hasOrgInPath = orgLink && orgLink !== 'homegyde' && currentPath.includes(`/${orgLink}`)
            const hasAppInPath = appLink && currentPath.includes(`/${appLink}`)

            // Build URL based on current path structure
            if (hasOrgInPath && hasAppInPath) {
                // Currently on /myorg/myapp - retain both
                finalLink = `/${orgLink}/${appLink}/${x.link}`
            } else if (hasOrgInPath && !hasAppInPath) {
                // Currently on /myorg (org-level page)
                if (pageAppId && appLink) {
                    // Navigating to an app page - include app
                    finalLink = `/${orgLink}/${appLink}/${x.link}`
                } else {
                    // Staying at org level
                    finalLink = `/${orgLink}/${x.link}`
                }
            } else if (!hasOrgInPath && hasAppInPath) {
                // Currently on /myapp (standalone app without org in URL)
                finalLink = `/${appLink}/${x.link}`
            } else if (standaloneApp && appLink) {
                // Standalone app mode
                finalLink = `/${appLink}/${x.link}`
            } else if (standaloneOrg && orgLink && orgLink !== 'homegyde') {
                // Standalone org mode
                if (appLink && pageAppId) {
                    finalLink = `/${orgLink}/${appLink}/${x.link}`
                } else {
                    finalLink = `/${orgLink}/${x.link}`
                }
            } else if (orgLink && orgLink !== 'homegyde') {
                // Default: include org if not homegyde
                if (appLink && (pageAppId || app)) {
                    finalLink = `/${orgLink}/${appLink}/${x.link}`
                } else {
                    finalLink = `/${orgLink}/${x.link}`
                }
            } else {
                // Fallback - just the page link
                finalLink = `/${x.link}`
            }
        }

        // Clean up any double slashes
        finalLink = finalLink.replace(/\/+/g, '/')

        // Ensure starts with /
        if (!finalLink.startsWith('/')) {
            finalLink = '/' + finalLink
        }

        logger.info('handleGoToContent: Navigating', {
            pageLink: x.link,
            currentPath,
            orgLink,
            appLink,
            customDomain,
            standaloneApp,
            standaloneOrg,
            finalLink,
            reload,
            isDifferentApp,
            isDifferentOrg
        })

        dispatch(navigate(finalLink))

        // Reload if navigating to a different context or explicitly requested
        if (reload || isDifferentApp || isDifferentOrg) {
            setTimeout(() => {
                window.location.reload()
            }, 100)
        }
    }

    async function handleCopy(content) {
        if ((app || orgPermissions) && (tab == EditorTab.Pages || tab == EditorTab.Blogs)) {
            if (!page?.id) {
                return
            }

            // Use our new local clone state instead of Redux and navigation
            handleOpenCloneMode(content, app, organization)
            return
        }
        const fixContent = (x) => {
            if (tab == EditorTab.Content) {

                let {
                    id, pageId, staticLink, ...y
                } = x
                y.elements = y.elements.map(fixContent)
                y.links = y.links?.map((x) => ({ ...x, id: null, dynamicContentId: null }))
                y.media = y.media?.map((x) => ({ ...x, id: null, dynamicContentId: null }))
                y.points = y.media?.map((x) => ({ ...x, id: null, dynamicContentId: null }))
                return y
            } else if (tab == EditorTab.Pages || tab == EditorTab.Blogs) {
                let {
                    id, ...y
                } = x
                y.name = `${y.name} (Copy)`
                y.link = `${y.link}-copy`
                let existingCount = pages.filter((z) => z.link.startsWith(y.link)).length
                if (existingCount > 0) {
                    y.link = `${y.link}-${existingCount + 1}`
                }
                
                return y
            }
        }
        const newContent = fixContent(fnc.copyObj(content))
        let ret = await handleAddContent(newContent, newContent.order + 1)

        let newPage = ret.find((x) => {
            return x.page.link.startsWith(newContent.link)
        })?.page

        if (newPage && (tab == EditorTab.Pages || tab == EditorTab.Blogs)) {
            let copyContent = hybridContent(app, null, organization, content, null)
            for (const x of copyContent) {
                copyContent = fnc.replaceObjectValue(copyContent, 'id', null)
                copyContent = fnc.replaceObjectValue(copyContent, 'pageId', newPage.id)
                copyContent = fnc.replaceObjectValue(copyContent, 'parentContentId', null)
            }
            handleGoToContent(newPage)
            handleAddContent(copyContent, -1, newPage, EditorTab.Content)
        }
    }

    function handleChange() {
        if (savingRef.current || Date.now() < suppressChangeUntilRef.current) {
            return
        }
        setPendingChange(true)
        setSaveText('Save')
        if (tab == EditorTab.Pages || tab == EditorTab.Blogs) {
            setEditPage(data)
            return
        }
        dispatch(updateEditContent(data))
    }

    function handleEditContent(content) {
        if (tab === EditorTab.Content) {
            dispatch(setEditContent(fnc.copyObj(content)))
            setEditPage(null)
        } else {
            setEditPage(fnc.copyObj(content))
        }
    }

    function handleAddAllPages() {
        const newPages = staticPages.map((x, ix) => ({
            ...x,
            pageTypeId: Object.values(config.pageTypes).findIndex((x) => x.name == "custom"),
            order: ix,
            organizationId: organization.id,
        }))
        handleSave(newPages)
    }

    function handleAddAllContent() {
        handleSave(pageContent.filter((x) => x.id == null))
    }

    async function handleAddPageContent() {
        const ret = await handleAddContent(null)
        if (ret.length > 0 && ret[0] != null) {
            dispatch(setEditContent(fnc.copyObj(ret[0])))
        }
    }

    async function handleAddHomePage() {
        const ret = await handleAddContent({ ...defaultPage, name: 'Home', link: 'home', pageTypeId: PageTypeId.Home }, -1, null, EditorTab.Pages)
        if (ret && ret[0]?.page) {
            handleGoToContent(ret[0].page)
        }
        return ret
    }

    async function handleAddPage(e, withAI = false) {
        if (withAI) {
            handleLLMHelper(e, LLMGenerateType.PageContent)
        } else {
            const ret = await handleAddContent(null)
            const newPage = ret[0]?.page
            if (newPage) {
                setEditPage(newPage)
                handleGoToContent(newPage)
            }
            return newPage
        }
    }

    async function handleAddContent(existing, insertIndex = -1, _page = null, _tab = tab) {
        let finalPage = _page ? _page : page
        let toSave = []
        let defaultData = null
        let dataArray = null
        switch (_tab) {
            case EditorTab.Content:
                if (!_page) {
                    dataArray = pageContent
                } else {
                    dataArray = []
                }
                defaultData = defaultContent
                break
            case EditorTab.Pages:
                dataArray = pages
                defaultData = { ...defaultPage, name: 'Page', link: 'page' }
                break
            case EditorTab.Blogs:
                dataArray = posts
                defaultData = { ...defaultPage, name: 'Post', link: 'post', pageType: PageType.Blog }
                break

            default:
                dataArray = []

        }

        const addHelper = (arr, newContent, exist) => {
            let maxOrder = arr.length > 0 ? Math.max(...arr?.map((x) => x.order)) : 0
            if (maxOrder == null) {
                maxOrder = 0
            }
            let newOrder = 0
            if (insertIndex >= 0 && insertIndex <= maxOrder && insertIndex != null) {
                newOrder = insertIndex
            } else if (exist && 'order' in exist && exist.order != null) {
                if (exist.staticLink) {
                    newOrder = exist.order + 1
                } else {
                    newOrder = exist.order
                }
            } else if (arr.length > 0) {
                newOrder = maxOrder + 1
            }

            if (insertIndex >= 0 && insertIndex <= maxOrder) {
                // Take all elements at and after index and increase order by 1
                arr.forEach((x) => {
                    if (x.order >= insertIndex) {
                        toSave.push({ ...x, order: x.order + 1 })
                    }
                })
            }
            newContent.order = newOrder

            if (!app) {
                newContent.organiziationId = organization.id
            } else {
                newContent.appId = app.meta.id
            }
            if (_tab == EditorTab.Content) {
                newContent.pageId = finalPage?.id
            } else {
                // Generate unique name and link for pages and blog posts
                const baseLink = newContent.link
                const baseName = newContent.name

                if (baseLink && (baseLink === 'page' || baseLink === 'post')) {
                    // Check for existing pages with the same base link
                    const existingLinks = arr.map(x => x.link).filter(link => link && link.startsWith(baseLink))

                    if (existingLinks.length > 0) {
                        // Find the highest number suffix
                        let maxSuffix = 0
                        existingLinks.forEach(link => {
                            if (link === baseLink) {
                                maxSuffix = Math.max(maxSuffix, 1)
                            } else {
                                const match = link.match(new RegExp(`^${baseLink}-(\\d+)$`))
                                if (match) {
                                    maxSuffix = Math.max(maxSuffix, parseInt(match[1]))
                                }
                            }
                        })

                        const suffix = maxSuffix + 1
                        newContent.name = `${baseName} ${suffix}`
                        newContent.link = `${baseLink}-${suffix}`
                    }
                }
            }

            return newContent
        }
        if (Array.isArray(existing)) {
            existing.forEach((x) => {
                toSave.push(addHelper(dataArray, { ...defaultData, ...x }, x))
            })
        } else {
            toSave.push(addHelper(dataArray, { ...defaultData, ...existing }, existing))
        }

        const ret = handleSave(toSave, false, _tab)
        return ret
        // return handleSave(toSave, false, _tab)

        // dispatch(upsertContent({ app, data: newContent }))
        // .then((x) => {
        // dispatch(updateEditContent(x.payload))
        // dispatch(updateAppContent(x.payload))
        // })
    }

    /**
     * Dedicated save function for reorder operations.
     * Bypasses the general handleSave to avoid interactions with saving/savingRef state
     * that affect other UI flows (edit forms, etc).
     */
    async function handleReorderSave(items: any[]) {
        logger.info('[Reorder] handleReorderSave:', items.map(x => `${x.id?.substring(0, 8)}→order:${x.order}`).join(', '))

        markReorderInProgress()

        try {
            // Single atomic dispatch: all API calls happen in parallel inside the thunk,
            // but Redux state only updates once when all are done.
            const result = await dispatch(batchUpsertContent({ app, organization, items }))
            if (result?.meta?.requestStatus === 'rejected' || result?.error) {
                logger.error('[Reorder] handleReorderSave - Batch save failed:', result)
            } else {
                logger.info('[Reorder] handleReorderSave - Batch save completed successfully')
            }
            return result
        } catch (e) {
            logger.error('[Reorder] handleReorderSave - Save failed:', e)
            throw e
        } finally {
            // Cancel any previous timer so only the LAST reorder's timeout clears the guard.
            // Without this, rapid reorders create multiple timeouts and an early one
            // clears the guard while a later reorder is still settling.
            if (reorderTimer.current) {
                clearTimeout(reorderTimer.current)
            }
            reorderTimer.current = setTimeout(() => {
                reorderInProgress.current = false
                reorderTimer.current = null
                logger.info('[Reorder] handleReorderSave - reorderInProgress cleared')
            }, 2000)
        }
    }

    function markReorderInProgress() {
        reorderInProgress.current = true
        if (reorderTimer.current) {
            clearTimeout(reorderTimer.current)
            reorderTimer.current = null
        }
    }

    function handleOrder(content, direction, isFooter = false) {
        logger.info(`[Reorder] handleOrder id=${content.id?.substring(0, 8)} order=${content.order} dir=${direction} footer=${isFooter}`)

        if (tab == EditorTab.Content) {
            const manager = isFooter ? getFooterReorderManager() : getPageReorderManager()
            manager.reorder(content.id, direction).then((result) => {
                if (result.success) {
                    const snapshot = manager.getSortedItems()
                    logger.info('[Reorder] post-swap:', snapshot.map(x => `${x.id?.substring(0, 8)}:${x.order}`).join(', '))
                }
            })
        } else if (tab == EditorTab.Pages) {
            logger.info('[Reorder] handleOrder - Processing Pages tab')
            // Pages/Blogs don't use the manager (less frequent, no race issues)
            handleLegacySwap(pages, content.id, direction, (items) => {
                handleSave(items)
            })
        } else if (tab == EditorTab.Blogs) {
            logger.info('[Reorder] handleOrder - Processing Blogs tab')
            handleLegacySwap(posts, content.id, direction, (items) => {
                handleSave(items)
            })
        }
    }

    /**
     * Fallback swap for pages/blogs tabs that don't need the full orchestrator.
     */
    function handleLegacySwap(arr, matchId, direction, saveFn) {
        if (!arr || arr.length === 0) return

        const sortedArr = [...arr].sort((a, b) => {
            if (a.order !== b.order) return a.order - b.order
            return String(a.id).localeCompare(String(b.id))
        })

        const currentIdx = sortedArr.findIndex((x) => x.id === matchId)
        if (currentIdx < 0) return

        const swapIdx = direction < 0 ? currentIdx - 1 : currentIdx + 1
        if (swapIdx < 0 || swapIdx >= sortedArr.length) return

        const currentElement = sortedArr[currentIdx]
        const swapElement = sortedArr[swapIdx]

        const newCurrent = { ...currentElement, order: swapElement.order }
        const newSwap = { ...swapElement, order: currentElement.order }

        saveFn([newCurrent, newSwap])
    }

    async function addLLMContent(content, replaceAll = false, newPage = null) {
        let finalData = []
        if (!Array.isArray(content)) {
            content = [content]
        }
        const maxOrder = pageContent?.length > 0 ? Math.max(...pageContent?.map((x) => x.order)) : 0
        let ix = 0
        let proms = []
        for (const x of content) {
            const validateObj = async (x, def, ix) => {
                logger.info('Validating', x, def)
                let issues = []
                let finalObj = { order: maxOrder + ix }
                if ('type' in x) {
                    finalObj.dynamicContentTypeId = x.type
                }

                if (def.title === 'Media' && x.url) {
                    try {
                        const url = x.url
                        proms.push(new Promise((resolve, reject) => {
                            downloadMedia(url)
                                .then((x) => {
                                    resolve({ original: url, replace: x })
                                }).catch(reject)
                        }))
                    } catch (e) {
                        logger.error("Error retrieving media", e)
                    }
                }

                logger.info("Final obj", fnc.copyObj(finalObj))

                for (const key of Object.keys(x)) {
                    if (key === 'type') continue

                    if (!(key in def.fields) && key !== 'url') {
                        logger.info('Field not in definition', key, def)
                        issues.push(`Field ${key} not in definition`)
                        continue
                    }

                    if (Array.isArray(x[key]) && def.fields[key].def) {
                        // Use a for loop to await each validation
                        for (let iy = 0; iy < x[key].length; iy++) {
                            try {
                                x[key][iy] = await validateObj({ ...x[key][iy], dynamicContentTypeId: x.dynamicContentTypeId || DynamicContentType.Element }, def.fields[key].def, iy)
                                // if (key == 'elements') {
                                // x[key][iy] = await validateObj({ ...x[key][iy], dynamicContentTypeId: x.dynamicContentTypeId || DynamicContentType.Element }, def.fields[key].def, iy)
                                // } else {
                                // x[key][iy] = await validateObj(x[key][iy], def.fields[key].def, iy)
                                // }
                            } catch (e) {
                                issues.push(`Error validating field ${key} at index ${iy}: ${e}`)
                            }
                        }
                        finalObj[key] = x[key]
                    } else {
                        finalObj[key] = x[key]
                    }
                }
                return finalObj
            }

            try {
                const result = await validateObj(x, contentDef, ix)
                // Replace media in result
                finalData.push(result)
            } catch (e) {
                logger.info('Validation error', e)
            }

            ix += 1
        }
        // Media replacement
        const mediaResult = await Promise.all(proms)
        if (mediaResult.length > 0) {
            let finalDataString = JSON.stringify(finalData)
            mediaResult.forEach((x) => {
                const { original, replace } = x
                const replacementMedia = { ...defaultMedia, ...replace }
                const properties = Object.keys(replacementMedia).map((x) => {
                    if (typeof replacementMedia[x] === "string") {
                        return `"${x}":"${replacementMedia[x]}"`
                    } else {
                        return `"${x}":${replacementMedia[x]}`
                    }
                }).join(",")
                finalDataString = finalDataString.replace(`"url":"${original}"`, properties).replace("\"null\"", "null").replace('\"true\"', 'true').replace('\"false\"', 'false')
            })
            finalData = JSON.parse(finalDataString)
        }
        handleAddContent(finalData, null, newPage || editPage, EditorTab.Content)
    }

    function handleLLMHelper(e, type) {
        let title = '';
        let defaultValue = 'Generate content related to a new home buyer.'
        switch (type) {
            case LLMGenerateType.SingleContent:
                title = 'Add AI Content';
                break
            case LLMGenerateType.PageContent:
                title = 'Generate AI Page';
                break
            case LLMGenerateType.SocialMediaPost:
                title = 'Generate AI Social Media Post';
                defaultValue = 'Generate a social media post for a new home buyer.';
                break
        }
        const dialogHeight = 600
        const newLLMHelper = {
            id: 'content',
            type,
            options: { defaultValue, data: pageContent },
            target: e.target,
            title
        }
        if (elem) {
            const rect = elem.getBoundingClientRect()
            newLLMHelper.left = rect.left + rect.width + 100
            newLLMHelper.top = Math.max(50, (window.innerHeight - dialogHeight) / 2 + window.scrollY)
        } else {
            newLLMHelper.left = e.pageX + 20
            newLLMHelper.top = Math.max(50, (window.innerHeight - dialogHeight) / 2 + window.scrollY)
        }
        setLLMHelper(newLLMHelper)
    }

    function handleLLMHelperClose() {
        setLLMHelper(null)
    }

    async function handleLLMHelperValue(x) {
        if (LLMHelper.type == LLMGenerateType.SingleContent) {
            // Add single content
            await addLLMContent(x)
        } else if (LLMHelper.type == LLMGenerateType.PageContent) {
            const newPage = await handleAddPage(null, false)
            // First we need to generat a page
            // Clear all content and replace with
            // if (LLMHelper.type == LLMGenerateType.PageContent && pageContent.length > 0) {
            //     // Clear content first
            //     setEditContent([]) 
            //     let proms = pageContent.map((y) => {
            //         return dispatch(removeContent({ app, organization, data: y }))
            //     })
            //     await Promise.all(proms)
            // }
            // Set page id for new content
            setGeneratingPage(true)
            setTimeout(async () => {
                await addLLMContent(x, false, newPage)
                setGeneratingPage(false)
            }, 250)
        }
        return null
        // return 'Content generated'
    }

    function handleSelectPage(x) {
        handleGoToContent(x)
    }

    function handleAddPageFromTemplate(e) {
        setTemplateMode(true)
    }

    function handleCloseTemplateMode() {
        setTemplateMode(false)
    }

    function handleOpenCloneMode(templatePage, sourceApp, sourceOrganization) {
        setCloneData({
            templatePage,
            sourceApp,
            sourceOrganization
        })
        setCloneMode(true)
    }

    function handleCloseCloneMode() {
        setCloneMode(false)
        setCloneData(null)
    }

    function handleCloneSuccess(result) {
        // Optional callback for when cloning succeeds
        logger.info('Template cloned successfully:', result)
    }

    async function handleCloneFromTemplate(templatePage) {
        // Get source context for the template
        const sourceApp = app
        const sourceOrganization = organization

        // Open clone mode with the template data
        handleOpenCloneMode(templatePage, sourceApp, sourceOrganization)
    }

    function getEditor() {
        // switch(editContent.type) {
        // }
        if (editContent) {
            const config = {
                app,
                organization
            }

            const finalData = {
                'content': [data]
            }
            const selection = {
                0: {
                    field: 'content',
                    fields: contentDef.fields,
                    value: editContent,
                }
            }
            // const def = getDef(editContent.type || editContent.dynamicContentTypeId)
            const def = getDef(editContent.dynamicContentTypeId)

            return <GenericEditor app={app} organization={organization} title={DynamicContentType[editContent.dynamicContentTypeId]} data={finalData} selection={selection} config={config} def={def} onChange={handleChange} noDelete onValidateRef={validateRef} />
        } else if (editPage) {
            const pageConfig = {
                app,
                organization,
                ...config
            }
            const finalData = {
                'pages': [data]
            }
            const selection = {
                0: {
                    field: 'pages',
                    fields: blogDef.fields,
                    value: editPage
                }
            }

            const defPages = fnc.copyObj(tab == EditorTab.Pages ? pagesRootDef : blogRootDef)
            const title = getPageTypeName(editPage, config)
            return <GenericEditor app={app} title={title} data={finalData} selection={selection} config={pageConfig} def={defPages} onChange={handleChange} noDelete onValidateRef={validateRef} />

        }
    }

    function getContentSelector() {
        // Check if any static content
        let hasStatic = false
        if (tab == EditorTab.Content) {
            hasStatic = pageContent?.find((x) => x.id == null)
        } else if (tab == EditorTab.Pages && organization && !app) {
            hasStatic = staticPages.length > 0
        }

        const source = app ? app : (organization ? organization : null)
        if (!source) {
            return
        }
        let minOrder = 0
        let maxOrder = 0
        if (tab == EditorTab.Content) {
            minOrder = pageContent?.reduce((a, b) => Math.min(a, b.order), 0) || 0
            maxOrder = pageContent?.reduce((a, b) => Math.max(a, b.order), 0) || 0
        } else if (tab == EditorTab.Pages) {
            minOrder = pages?.reduce((a, b) => Math.min(a, b.order), 0) || 0
            maxOrder = pages?.reduce((a, b) => Math.max(a, b.order), 0) || 0
        } else if (tab == EditorTab.Blogs) {
            minOrder = posts?.reduce((a, b) => Math.min(a, b.order), 0) || 0
            maxOrder = posts?.reduce((a, b) => Math.max(a, b.order), 0) || 0
        }

        return <div className="content-editor scrollable" ref={setListElem}>
            <div className="content-editor-topbar">
                <div className="content-editor-title">
                    <h4>{EditorTab[tab]}</h4>
                </div>
            </div>
            {tab == EditorTab.Blogs && pages && <div className="page-content-list">
                {[...posts].sort((a, b) => a.order - b.order).map((x, ix) => {
                    const title = getPageTypeName(x, config)

                    return <div className="row page-content column" id={`${x.id}-${ix}`}>
                        <span className="row page-type">
                            {title?.toReadable()} - {x.order}{x.draft ? ` (DRAFT)` : ''}
                        </span>
                        <div className="row">
                            <div className="column" style={{ width: 'auto' }}>
                                <Icon className="page-icon" noBg icon={x.icon ? x.icon : pageIcons[title]} />
                            </div>
                            <div className="column" style={{ width: '100%' }}>
                                <h4>{x.name}</h4>
                                {/*(x.exclusive || x.draft || x.salescenterOnly) && <ul>
                                {x.exclusive && <li>VIP</li>
                                {x.draft && <li>Draft</li>
                                {x.salescenterOnly && <li>Sales Center Only</li>
            </ul>*/}
                            </div>
                        </div>
                        <div className="bottom right page-content-options">
                            <React.Fragment>
                                <IconButton noBg icon="fas fa-arrow-right" onClick={() => handleGoToContent(x)} />
                                <IconButton noBg icon="fas fa-pen" onClick={() => handleEditContent(x)} />
                                {x.order > minOrder && <IconButton noBg icon="fas fa-arrow-up" onClick={() => handleOrder(x, -1)} />}
                                {x.order < maxOrder && <IconButton noBg icon="fas fa-arrow-down" onClick={() => handleOrder(x, 1)} />}
                                <IconButton noBg icon="fas fa-clone" onClick={() => handleCopy(x)} />
                                <IconButton noBg icon="fas fa-trash" onClick={() => handleDelete(x)} />
                            </React.Fragment>
                        </div>
                    </div>
                })}
                <div className="row">
                    <Button className="add-content" icon="fas fa-plus" tertiary onClick={(e) => handleAddPage(e, false)}>Add Blog Manually</Button>
                    <DocLinkButton docLink="manualBlog" />
                </div>
                <div className="row">
                    <Button className="add-content" icon="fas fa-plus" tertiary onClick={(e) => handleAddPageFromTemplate(e)}>Add Blog From Template</Button>
                    <DocLinkButton docLink="templateBlog" />
                </div>
                <div className="row">
                    <Button className="add-content" icon="fas fa-hand-sparkles" tertiary disabled={saving || editing} onClick={(e) => handleAddPage(e, true)}>Add Blog using AI</Button>
                    <DocLinkButton docLink="aiBlog" />
                </div>
                <div className="row">
                    <DocLinkButton className="add-content" tertiary docLink="content">Documentation</DocLinkButton>
                </div>
            </div>}
            {tab == EditorTab.Pages && pages && <div className="page-content-list">
                {hasStatic && <div className={`row page-content column`} onClick={() => handleAddAllPages()}>
                    <h5>Add All Static Pages</h5>
                </div>}
                {[...pages].sort((a, b) => a.order - b.order).map((x, ix) => {
                    const isStatic = x.id == null
                    const title = getPageTypeName(x, config)

                    return <div className="row page-content column" id={`${x.id}-${ix}`}>
                        <span className="row page-type">
                            {title?.toReadable()} - {x.order}{x.draft ? ` (DRAFT)` : ''}
                        </span>
                        <div className="row">
                            <div className="column" style={{ width: 'auto' }}>
                                <Icon className="page-icon" noBg icon={x.icon ? x.icon : pageIcons[title]} />
                            </div>
                            <div className="column" style={{ width: '100%' }}>
                                <h4>{x.name}</h4>
                                {(x.exclusive || x.draft || x.salescenterOnly) && <ul>
                                    {x.exclusive && <li>VIP</li>}
                                    {x.draft && <li>Draft</li>}
                                    {x.salescenterOnly && <li>Sales Center Only</li>}
                                </ul>}
                            </div>
                        </div>
                        <div className="bottom right page-content-options">
                            {!isStatic && <React.Fragment>
                                <IconButton noBg icon="fas fa-arrow-right" onClick={() => handleGoToContent(x)} />
                                <IconButton noBg icon="fas fa-pen" onClick={() => handleEditContent(x)} />
                                {x.order > minOrder && <IconButton noBg icon="fas fa-arrow-up" onClick={() => handleOrder(x, -1)} />}
                                {x.order < maxOrder && <IconButton noBg icon="fas fa-arrow-down" onClick={() => handleOrder(x, 1)} />}
                                <IconButton noBg icon="fas fa-clone" onClick={() => handleCopy(x)} />
                                <IconButton noBg icon="fas fa-trash" onClick={() => handleDelete(x)} />
                            </React.Fragment>}
                            {isStatic && <IconButton noBg icon="fas fa-plus" onClick={() => handleAddContent(x)} />}
                        </div>
                    </div>
                })}
                <div className="row">
                    <Button className="add-content" icon="fas fa-plus" tertiary onClick={(e) => handleAddPage(e, false)}>Add Page Manually</Button>
                    <DocLinkButton docLink="manualPage" />
                </div>
                <div className="row">
                    <Button className="add-content" icon="fas fa-plus" tertiary onClick={(e) => handleAddPageFromTemplate(e)}>Add Page From Template</Button>
                    <DocLinkButton docLink="templatePage" />
                </div>
                <div className="row">
                    <Button className="add-content" icon="fas fa-hand-sparkles" tertiary disabled={saving || editing} onClick={(e) => handleAddPage(e, true)}>Add Page using AI</Button>
                    <DocLinkButton docLink="aiPage" />
                </div>
                <div className="row">
                    <DocLinkButton className="add-content" tertiary docLink="content">Documentation</DocLinkButton>
                </div>
            </div>}
            {tab == EditorTab.Content && !page && <div className="page-content-list">
                <h5 style={{ margin: '20px', width: 'auto', whitespace: 'pre-wrap' }}>Please navigate to valid page</h5>
                {app?.pages.map((x) => <div className="row">
                    <Button tertiary onClick={() => handleSelectPage(x)}>{x.name}</Button>
                </div>)}
                {!app && organization?.pages.map((x) => <div className="row">
                    <Button tertiary onClick={() => handleSelectPage(x)}>{x.name}</Button>
                </div>)}
            </div>}
            {tab == EditorTab.Content && page && <div className="page-content-list">

                {hasStatic && <div className={`row page-content column`} onClick={() => handleAddAllContent()}>
                    <h5>Add All Static Content</h5>
                </div>}
                {pageContent && [...pageContent].sort((a, b) => a.order - b.order).map((x, ix) => {
                    const contentType = x.dynamicContentTypeId || x.type
                    const isStatic = x.id == null

                    return <div className={`row page-content column${x.id == null ? ' static' : ''}`} data-id={x.id} onClick={() => handleFocusContent(x)}>
                        <span className="row page-type">
                            {DynamicContentType[contentType]?.toReadable()} - {x.order} - {x.staticLink}{x.draft ? ` (DRAFT)` : ''}
                        </span>
                        {x.title && x.title.length > 0 && <div className="row">
                            <h4>{x.title}</h4>
                        </div>}
                        {x.body && x.body.length > 0 && <div className="row">
                            {x.body && x.body.length > 50 && <span>{x.body.slice(0, 50)}...</span>}
                            {x.body && x.body.length <= 50 && <span>{x.body}</span>}
                        </div>}
                        {x.media && <div className="row page-content-media-wrapper">
                            {x.media.map((y) => {

                                let mediaElem = null
                                if (y.mediaId != null) {
                                    mediaElem = <Media mediaId={y.mediaId} thumb={true} thumbSize={ThumbSize.Small} app={app} organization={organization} />
                                } else if (y.galleryId != null) {
                                    const gallery = source.galleries.find((z) => z.id == y.galleryId)
                                    if (gallery?.media.length > 0) {
                                        mediaElem = gallery.media.slice(0, 3).map((z) => { return <Media mediaId={z.mediaId} thumb={true} thumbSize={ThumbSize.Small} app={app} organization={organization} /> })
                                    }
                                }
                                if (mediaElem != null) {
                                    return <div className="column page-content-media">{mediaElem}</div>
                                }
                            })}
                        </div>
                        }

                        <div className="bottom right page-content-options">
                            {!isStatic && <React.Fragment>
                                <IconButton noBg icon="fas fa-pen" onClick={() => handleEditContent(x)} />
                                {x.order > minOrder && <IconButton noBg icon="fas fa-arrow-up" onClick={() => handleOrder(x, -1)} />}
                                {x.order < maxOrder && <IconButton noBg icon="fas fa-arrow-down" onClick={() => handleOrder(x, 1)} />}
                                <IconButton noBg icon="fas fa-clone" onClick={() => handleCopy(x)} />
                                <IconButton noBg icon="fas fa-trash" onClick={() => handleDelete(x)} />
                            </React.Fragment>}
                            {isStatic && <IconButton noBg icon="fas fa-plus" onClick={() => handleAddContent(x)} />}
                        </div>

                    </div>
                })}
                <div className="row">
                    <Button className="add-content" icon="fas fa-plus" tertiary onClick={() => handleAddPageContent()}>Add Content Manually</Button>
                    <DocLinkButton docLink="manualContent" />
                </div>
                <div className="row">
                    <Button className="add-content" icon="fas fa-hand-sparkles" tertiary disabled={saving || editing} onClick={(e) => handleLLMHelper(e, LLMGenerateType.SingleContent)}>Add Content using AI</Button>
                    <DocLinkButton docLink="aiContent" />
                </div>
                <div className="row">
                    <Button className="add-content" icon="fas fa-hand-sparkles" tertiary disabled={saving || editing} onClick={(e) => handleLLMHelper(e, LLMGenerateType.SocialMediaPost)}>Generate Social Media Post using AI</Button>
                    {/* <DocLinkButton docLink={docLinks.link} /> */}
                </div>
                <div className="row">
                    <DocLinkButton className="add-content" tertiary docLink="content">Documentation</DocLinkButton>
                </div>
            </div>}
        </div>
    }

    if (show == null) {
        return
    }
    const editing = editContent || editPage

    if (templateMode) {
        return (
            <ErrorBoundary>
                <TemplateSelector
                    organization={organization}
                    config={config}
                    onClose={handleCloseTemplateMode}
                    onNavigate={handleGoToContent}
                />
            </ErrorBoundary>
        )
    }

    if (cloneMode) {
        return (
            <ErrorBoundary>
                <CloneTemplateSelector
                    templatePage={cloneData?.templatePage}
                    sourceApp={cloneData?.sourceApp}
                    sourceOrganization={cloneData?.sourceOrganization}
                    onClose={handleCloseCloneMode}
                    onCloneSuccess={handleCloneSuccess}
                    onNavigate={handleGoToContent}
                />
            </ErrorBoundary>
        )
    }

    let tabItems = [
        { value: EditorTab.Content, icon: "fas fa-pen", text: 'Content' },
        { value: EditorTab.Pages, icon: "fas fa-sticky-note", text: 'Pages' },
        { value: EditorTab.Blogs, icon: "fas fa-newspaper", text: 'Blogs' }
    ]

    return <React.Fragment>
        <div className={`inline-content-editor column${expanded ? ' expanded' : ''}${saving ? ' saving' : ''}`} key={tab} ref={setElem}>
            <ErrorBoundary>
                {generatingPage && <Spinner invert showAfter={0} />}
                {LLMHelper && <LLMContentHelper {...LLMHelper} onSubmit={handleLLMHelperValue} onClose={handleLLMHelperClose} app={app} organization={organization} />}
                <div className="top right">
                    {(saving || (editing && pendingChange && data)) && <Button className="animate__animated animate__fadeInUp animate__fastest" onClick={() => handleSave(data)} tertiary={!saving}>
                        {saveText}
                        {saving && <Spinner showAfter={0} />}
                    </Button>}
                </div>
                <div className="top left">
                    {!editing && <IconButton noBg icon={"fas fa-times"} onClick={handleClose} />}
                    {editing && <IconButton noBg icon="fas fa-arrow-left" onClick={handleCancel} />}
                </div>
                {editing && getEditor()}
                {!editing && getContentSelector()}
                {!editing && !nested && <div className="row editor-tabs">
                    <Toggle value={tab} onChange={setTab} items={tabItems} />
                </div>}
            </ErrorBoundary>
        </div>
    </React.Fragment>
}
